/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { AxiosRequestConfig, AxiosError } from 'axios'
import { APP_ENDPOINT, APP_ENV, FIVE_9_URL, IS_PRODUCTION } from '../constants'
import {
  CallInfo,
  CampaignInfo,
  Five9DataCenter,
  five9DisconnectAndDisposeCall,
  five9GetCalls,
  five9GetOrgCampaigns,
  five9GetLoginState,
  five9GetStation,
  five9HoldCall,
  five9Login,
  Five9LoginResponse,
  five9LogoutAllDevices,
  five9MakeExternalCall,
  five9Metadata,
  five9ParkCall,
  five9RestartStation,
  five9RetrieveCall,
  five9UnholdCall,
  LoginState,
  StationInfo,
  StationState,
  five9TransferToCampaign,
  five9AnswerCall,
  five9RejectCall,
  five9CompleteTransfer,
  five9GetPresence,
  AgentPresenceInfo,
  five9GetOrgReasonCodes,
  five9GetSkills,
  five9UpdatePresence,
  five9AddExternalParticipant,
  five9AddCampaignToConference,
  five9CompleteWarmConference,
  five9EndCall,
  five9ExternalTransfer,
  isFive9ExceptionResponse,
  five9CancelWarmTransfer,
  Five9CallState,
  five9DisconnectConferenceParticipant,
  five9CreateContact,
  five9GetContactByConsultId,
  CreateContactPayload,
  five9DeleteContact,
  five9SelectActiveContactById,
  five9LeaveConference,
} from './Services/Five9Service'
import {
  createEventAwaiter,
  createFive9WebSocket,
  FIVE9_PING_INTERVAL,
  Five9EventType,
  ping,
} from './Services/Five9WebSocket'
import { useLocation } from 'react-router-dom'
import { Consult } from './GraphQL/Consult'

interface Five9SessionData {
  orgId: string
  userId: string
  tokenId: string
  farmId: string
  sessionId: string
  activeDataCenter: Five9DataCenter | undefined
}
export enum AgentAvailability {
  READY = 'Ready',
  NOT_READY = 'Not Ready',
  ON_BREAK = 'On Break',
  ADDITIONAL_CALL_WORK = 'Additional Call Work',
  SPECIAL_PROJECT = 'Special project',
  SYSYEM_ISSUES = 'System issues',
  PCM_PRE_CALL_WORK = 'Pre-call work',
  PCM_OUTBOUND = 'Outbound',
  PCM_POST_CALL_WORK = 'Post-call work',
  NOT_CONNECTED = 'Not connected',
}

interface AgentPrecenseCodesInterface {
  READY: string
  NOT_READY: string
  ON_BREAK: string
  ADDITIONAL_CALL_WORK: string
  SPECIAL_PROJECT: string
  SYSTEM_ISSUES: string
  PCM_PRE_CALL_WORK: string
  PCM_OUTBOUND: string
  PCM_POST_CALL_WORK: string
}
const AgentPrecenseCodesStaging: Readonly<AgentPrecenseCodesInterface> = {
  READY: '0',
  NOT_READY: '-1',
  ON_BREAK: '435180',
  ADDITIONAL_CALL_WORK: '300000000000003',
  SPECIAL_PROJECT: '300000000000004',
  SYSTEM_ISSUES: '300000000000005',
  PCM_PRE_CALL_WORK: '300000000000026',
  PCM_OUTBOUND: '300000000000028',
  PCM_POST_CALL_WORK: '300000000000027',
}
const AgentPrecenseCodesProduction: Readonly<AgentPrecenseCodesInterface> = {
  READY: '0',
  NOT_READY: '-1',
  ON_BREAK: '392310',
  ADDITIONAL_CALL_WORK: '395917',
  SPECIAL_PROJECT: '395898',
  SYSTEM_ISSUES: '395899',
  PCM_PRE_CALL_WORK: '396017',
  PCM_OUTBOUND: '396018',
  PCM_POST_CALL_WORK: '396019',
}

export const AgentPrecenseCodes =
  APP_ENV === 'production'
    ? AgentPrecenseCodesProduction
    : AgentPrecenseCodesStaging

export const MEMBER_SERVICES_NUMBER = IS_PRODUCTION
  ? '8006434845'
  : '8002211212'

export interface Five9State {
  sessionData: Five9SessionData
  station?: StationInfo
  loginState?: LoginState
  isWebSocketReady: boolean
  five9Calls?: CallInfo[]
  // five9NewOutboundCall is a newly created outbound call that is captured from websocket event
  five9NewOutboundCall: CallInfo | undefined
  five9DisconnectedCall: CallInfo | undefined
  login: (
    email: string,
    password: string
  ) => Promise<Five9LoginResponse | undefined>
  five9SessionActive: boolean
  logoutAllDevices: () => Promise<void>
  isGettingSkills: boolean
  getSkills: () => Promise<void>
  isGettingCalls: boolean
  getCalls: () => Promise<CallInfo[]>
  makeCall: (
    number: string,
    campaignId: string,
    contactId?: string
  ) => Promise<any>
  answerCall: (callId: string) => Promise<any>
  rejectCall: (callId: string) => Promise<any>
  holdCall: (callId: string) => Promise<any>
  unholdCall: (callId: string) => Promise<any>
  parkCall: (callId: string) => Promise<any>
  retrieveCall: (callId: string) => Promise<any>
  disconnectAndDisposeCall: (
    callId: string,
    dispositionId: string
  ) => Promise<any>
  getOrgCampaigns: () => Promise<CampaignInfo[]>
  transferToCampaign: (
    callId: string,
    campaignId: string,
    warm: boolean
  ) => Promise<any>
  completeTransfer: (callId: string) => Promise<any>
  agentAvailability?: AgentAvailability
  agentSkills?: string[]
  orgCampaigns?: CampaignInfo[]
  updateAgentPresence: (
    notReadyReasonCodeId: string,
    awaitConfirmation?: boolean
  ) => Promise<void>
  addExternalParticipant: (
    callId: string,
    number: string,
    warm?: boolean
  ) => Promise<any>
  completeWarmConference: (callId: string) => Promise<any>
  endFive9Call: (callId: string) => Promise<any>
  setCalls: (calls: CallInfo[]) => void
  five9TransferredCall: CallInfo | undefined
  externalTransfer: (
    callId: string,
    number: string,
    warm?: boolean
  ) => Promise<any>
  cancelWarmTransfer: (callId: string) => Promise<any>
  conferenceParticipantState?: Five9CallState
  disconnectConferenceParticipant: (callId: string) => Promise<any>
  createContact: (contactData: CreateContactPayload) => Promise<any>
  deleteContact: (contactId: string) => Promise<any>
  lookupContactByConsultId: (consultId: string) => Promise<any>
  five9InboundCall: CallInfo | undefined
  lookupContactAndSelectActive: (
    consult: Consult,
    callId: string
  ) => Promise<any>
  addCampaignToConference: (
    callId: string,
    campaignId: string,
    warm?: boolean
  ) => Promise<any>
  leaveConference: (callId: string, dispositionId: string) => Promise<any>
}

export const Five9Context = createContext<Five9State>({} as Five9State)

export const Five9ContextProvider = ({
  children,
}: {
  children: React.ReactChild
}) => {
  const [webSocket, setWebSocket] = useState<WebSocket | undefined>(undefined)
  const [isWebSocketReady, setIsWebSocketReady] = useState<boolean>(false)
  const lastPongDate = useRef<Date | undefined>(undefined)
  const [station, setStation] = useState<StationInfo | undefined>(undefined)
  const [loginState, setLoginState] = useState<LoginState | undefined>(
    undefined
  )
  const [conferenceParticipantState, setConferenceParticipantState] = useState<
    Five9CallState | undefined
  >(undefined)
  const [agentPresence, setAgentPresence] = useState<
    AgentPresenceInfo | undefined
  >(undefined)
  const [agentSkills, setAgentSkills] = useState<string[] | undefined>(
    undefined
  )
  const [isGettingSkills, setIsGettingSkills] = useState<boolean>(false)
  const [orgCampaigns, setOrgCampaigns] = useState<CampaignInfo[] | undefined>(
    undefined
  )
  const [agentAvailability, setAgentAvailability] = useState<
    AgentAvailability | undefined
  >(undefined)
  // set local storage for session data then update state
  const [sessionData, setSessionData] = useState<Five9SessionData>(
    {} as Five9SessionData
  )
  const [isGettingCalls, setIsGettingCalls] = useState<boolean>(false)
  const [calls, setCalls] = useState<CallInfo[] | undefined>(undefined)
  const [disconnectedCall, setDisconnectedCall] = useState<
    CallInfo | undefined
  >(undefined)
  const [newOutboundCall, setNewOutboundCall] = useState<CallInfo | undefined>(
    undefined
  )

  const [transferredCall, setTransferredCall] = useState<CallInfo | undefined>(
    undefined
  )
  const [five9InboundCall, setFive9InboundCall] = useState<
    CallInfo | undefined
  >(undefined)
  const [five9SessionActive, setFive9SessionActive] = useState<boolean>(
    sessionData.tokenId !== undefined &&
      webSocket?.readyState === WebSocket.OPEN
  )
  useEffect(() => {
    setFive9SessionActive(
      sessionData.tokenId !== undefined && loginState === LoginState.WORKING
    )
  }, [sessionData.tokenId, loginState])

  useEffect(() => {
    const interval = setInterval(() => {
      if (webSocket?.readyState !== WebSocket.OPEN) {
        setIsWebSocketReady(false)
        return
      }
      if (lastPongDate.current) {
        const now = new Date()
        const timeDifference = now.getTime() - lastPongDate.current.getTime()
        const limit = 2.2 * FIVE9_PING_INTERVAL
        if (timeDifference > limit) {
          ping(webSocket)
          // 2 missed pongs
          setIsWebSocketReady(false)
        }
      }
    }, 2000) // Check every 2 seconds

    return () => clearInterval(interval) // Cleanup interval on component unmount
  }, [webSocket, webSocket?.readyState])

  const getFive9RequestConfig = useCallback(() => {
    const config: AxiosRequestConfig = {
      baseURL: `https://${sessionData.activeDataCenter?.apiUrls[0].host}`,
      timeout: 10000,
      headers: {
        Accept: 'application/json, text/javascript, */*;',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    }
    return config
  }, [sessionData.activeDataCenter])

  const getLoginState = async () => {
    const response = await five9GetLoginState(
      sessionData.userId,
      getFive9RequestConfig()
    )
    setLoginState(response)
    return response
  }

  const getStation = async () => {
    if (!sessionData.userId) {
      return
    }
    const response = await five9GetStation(
      sessionData.userId,
      getFive9RequestConfig()
    )
    if (response?.stationId) {
      if (
        station?.stationId !== response.stationId ||
        station?.state !== response.state ||
        station?.stationType !== response.stationType
      ) {
        setStation(response)
      }
      setStation(response)
    } else {
      setStation(undefined)
    }
    return response
  }

  const getPresence = async () => {
    if (!sessionData.userId) {
      return
    }
    const response = await five9GetPresence(
      sessionData.userId,
      getFive9RequestConfig()
    )
    if (response) {
      setAgentPresence(response)
    } else {
      setAgentPresence(undefined)
    }
  }

  const updateAgentPresence = async (
    notReadyReasonCodeId: string,
    awaitConfirmation = false
  ) => {
    if (!sessionData.userId) {
      return
    }
    const presenceUpdatedEvent = createEventAwaiter(
      webSocket!,
      ({ context, payLoad }) => {
        return (
          context.eventId === Five9EventType.PRESENCE_UPDATED &&
          context.eventReason === 'AGENT' &&
          payLoad.currentState.notReadyReasonCodeId === notReadyReasonCodeId
        )
      }
    )

    await five9UpdatePresence(
      sessionData.userId,
      { notReadyReasonCodeId },
      getFive9RequestConfig()
    )

    if (awaitConfirmation) {
      try {
        await presenceUpdatedEvent()
      } catch (e) {
        // If the event didin't arrive or was missed, move on
        // so we don't block the agent
        console.log(e, 'Moving on.')
      }
    }
  }

  const getSkills = async () => {
    if (!sessionData.userId) {
      return
    }
    setIsGettingSkills(true)
    try {
      const response = await five9GetSkills(
        sessionData.userId,
        getFive9RequestConfig()
      )
      if (response) {
        setAgentSkills(response)
      } else {
        setAgentSkills(undefined)
      }
    } finally {
      setIsGettingSkills(false)
    }
  }

  useEffect(() => {
    switch (agentPresence?.currentState.notReadyReasonCodeId) {
      case AgentPrecenseCodes.READY:
        setAgentAvailability(AgentAvailability.READY)
        break
      case AgentPrecenseCodes.NOT_READY:
        setAgentAvailability(AgentAvailability.NOT_READY)
        break
      case AgentPrecenseCodes.ON_BREAK:
        setAgentAvailability(AgentAvailability.ON_BREAK)
        break
      case AgentPrecenseCodes.PCM_OUTBOUND:
        setAgentAvailability(AgentAvailability.PCM_OUTBOUND)
        break
      case AgentPrecenseCodes.PCM_PRE_CALL_WORK:
        setAgentAvailability(AgentAvailability.PCM_PRE_CALL_WORK)
        break
      case AgentPrecenseCodes.PCM_POST_CALL_WORK:
        setAgentAvailability(AgentAvailability.PCM_POST_CALL_WORK)
        break
      case AgentPrecenseCodes.ADDITIONAL_CALL_WORK:
        setAgentAvailability(AgentAvailability.ADDITIONAL_CALL_WORK)
        break
      case AgentPrecenseCodes.SPECIAL_PROJECT:
        setAgentAvailability(AgentAvailability.SPECIAL_PROJECT)
        break
      case AgentPrecenseCodes.SYSTEM_ISSUES:
        setAgentAvailability(AgentAvailability.SYSYEM_ISSUES)
        break
      default:
        setAgentAvailability(AgentAvailability.NOT_CONNECTED)
    }
  }, [agentPresence])

  const getMetadata = async () => {
    const config: AxiosRequestConfig = {
      baseURL: FIVE_9_URL,
      timeout: 10000,
      headers: {
        Accept: 'application/json, text/javascript, */*;',
      },
      withCredentials: true,
    }
    try {
      const response = await five9Metadata(config)
      if (
        sessionData.userId !== response.userId ||
        sessionData.tokenId !== response.tokenId ||
        sessionData.farmId !== response.context.farmId ||
        sessionData.sessionId !== response.sessionId
      ) {
        setSessionData({
          orgId: response.orgId,
          userId: response.userId,
          tokenId: response.tokenId,
          farmId: response.context.farmId,
          sessionId: response.sessionId,
          activeDataCenter: response.metadata.dataCenters.find(
            (dc) => dc.active
          ),
        })
      }
      return response
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response?.status === 401) {
        if (JSON.stringify(sessionData) !== '{}') {
          clearAllStates()
        }
      }
    }
  }

  const getCalls = useCallback(async () => {
    setIsGettingCalls(true)
    const response = await five9GetCalls(
      sessionData.userId,
      getFive9RequestConfig()
    )
    setCalls(response)
    // Check if there is at least one inbound call
    if (Array.isArray(response)) {
      const inbound = response.find(
        (call) => call.callType === 'INBOUND' || call.callType === 'TEST'
      )
      setFive9InboundCall(inbound)
    }
    setIsGettingCalls(false)
    return response
  }, [sessionData.userId, getFive9RequestConfig])

  const onCallUpdated = (call: CallInfo, reason?: string) => {
    if (
      reason === 'DISCONNECTED_BY_AGENT' ||
      reason === 'DISCONNECTED_BY_CALLER'
    ) {
      setDisconnectedCall(call)
    }

    // its important to keep this implementation for setCalls with current value
    setCalls((current) => {
      if (!current) {
        return [call]
      }
      return current.find((c) => c.id === call.id)
        ? current.map((c) => (c.id === call.id ? call : c))
        : [...current, call]
    })
  }

  useEffect(() => {
    if (calls && calls.length > 1) {
      setConferenceParticipantState(calls[1].state)
    } else {
      setConferenceParticipantState(undefined)
    }
  }, [calls])

  const onCallCreated = async (call: CallInfo) => {
    if (
      call.callType === 'INBOUND' ||
      (APP_ENV !== 'production' && call.callType === 'TEST')
    ) {
      setFive9InboundCall(call)
    } else {
      setNewOutboundCall(call)
    }
    setCalls((current) => {
      if (!current) {
        return [call]
      }
      return current.find((c) => c.id === call.id)
        ? current.map((c) => (c.id === call.id ? call : c))
        : [...current, call]
    })
    onCallUpdated(call)
  }

  const onCallDeleted = (call: CallInfo, reason?: string) => {
    if (reason === 'TRANSFERRED') {
      setTransferredCall(call)
    } else if (
      reason === 'DISCONNECTED_BY_AGENT' ||
      reason === 'DISCONNECTED_BY_CALLER'
    ) {
      setDisconnectedCall(call)
    } else if (reason === 'CONFERENCE_LEFT') {
      if (!calls?.length) {
        setTransferredCall(call)
      }
    }
    // its important to keep this implementation for setCalls with current value
    setCalls((prevCalls) => {
      if (!prevCalls) {
        return prevCalls
      }
      const updatedCalls = prevCalls.filter((c) => c.id !== call.id)
      return prevCalls.length === updatedCalls.length ? prevCalls : updatedCalls // Avoid unnecessary updates
    })
    setFive9InboundCall((previousInbound) =>
      previousInbound?.id === call.id ? undefined : previousInbound
    )
    setNewOutboundCall((previousOutbound) =>
      previousOutbound?.id === call.id ? undefined : previousOutbound
    )
  }

  const onStationUpdated = (station: StationInfo) => {
    setStation(station)
  }

  const onPresenceUpdated = (presence: AgentPresenceInfo) => {
    setAgentPresence(presence)
  }

  const onSocketOpened = (socket: WebSocket) => {
    setWebSocket(socket)
  }

  const onSocketClosed = async () => {
    getLoginState().catch((error: AxiosError) => {
      const responseData = error.response?.data
      if (
        isFive9ExceptionResponse(responseData) &&
        responseData.five9ExceptionDetail.errorCode === 401
      ) {
        clearAllStates()
      }
    })
  }

  const onPong = useCallback(async () => {
    lastPongDate.current = new Date()

    // Check current state first to avoid unnecessary work
    setIsWebSocketReady((currentIsReady) => {
      if (!currentIsReady) {
        // if isWebSocketReady has just become ready, the "calls" state is outdated
        // need to refetch calls before setting ready to true
        getCalls().then(() => {
          setIsWebSocketReady(true)
        })
        return false // Keep it false until getCalls completes
      }
      return currentIsReady
    })
  }, [getCalls])

  const tryConnectWebSocket = (webSocketNumber: number) => {
    createFive9WebSocket(
      sessionData.activeDataCenter!.apiUrls[0].host,
      sessionData.farmId,
      webSocketNumber,
      () => {
        tryConnectWebSocket(webSocketNumber + 1)
      },
      onCallCreated,
      onCallUpdated,
      onCallDeleted,
      onStationUpdated,
      onPresenceUpdated,
      onSocketOpened,
      onSocketClosed,
      onPong
    )
  }

  const location = useLocation()

  // handles socket connection/disconnection for router navigation (non-browser navigation)
  useEffect(() => {
    if (
      location.pathname.startsWith('/worklist') &&
      sessionData.activeDataCenter
    ) {
      if (!webSocket || webSocket.readyState === webSocket.CLOSED) {
        // If we are navigating into the PCM, and there's no websocket open, connect it.
        tryConnectWebSocket(1)
      }
    } else {
      // Close the websocket if we are navigating out of the PCM
      webSocket?.close()
      setWebSocket(undefined)
    }
  }, [location, sessionData])

  useEffect(() => {
    if (sessionData.userId) {
      if (loginState === LoginState.WORKING) {
        getStation()
        getCalls()
        getOrgCampaigns()
        getOrgReasonCodes()
        getPresence()
        getSkills()
      } else {
        getLoginState()
      }
    }
  }, [loginState, sessionData])

  const restartStation = async () => {
    if (!sessionData.userId) {
      return
    }
    await five9RestartStation(sessionData.userId, getFive9RequestConfig())
    await getStation()
  }

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (webSocket) {
        webSocket.close()
      }
      setWebSocket(undefined)
      setIsWebSocketReady(false)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('freeze', handleBeforeUnload)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('freeze', handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    getMetadata()
  }, [sessionData])

  useEffect(() => {
    if (
      loginState === LoginState.WORKING &&
      station?.state === StationState.DISCONNECTED
    ) {
      restartStation()
    }
  }, [loginState, station])

  const login = async (
    email: string,
    password: string
  ): Promise<Five9LoginResponse | undefined> => {
    const config: AxiosRequestConfig = {
      baseURL: FIVE_9_URL,
      timeout: 10000,
      headers: {
        Accept: 'application/json, text/javascript, */*;',
      },
      withCredentials: true,
    }
    const response = await five9Login(email, password, config)
    if (response) {
      setSessionData({
        orgId: response.orgId,
        userId: response.userId,
        tokenId: response.tokenId,
        farmId: response.context.farmId,
        sessionId: response.sessionId,
        activeDataCenter: response.metadata.dataCenters.find((dc) => dc.active),
      })
      return response
    }
  }

  const logoutAllDevices = async (): Promise<void> => {
    if (sessionData.tokenId) {
      await five9LogoutAllDevices(getFive9RequestConfig()) // the response header wipes the cookie
    }
    clearAllStates()
  }

  const clearAllStates = () => {
    // this does not clear cookie. there is no way to clear cookie of .five9.com domain
    // so we cannot use this function to wipe the session on the app
    // this is only a helper function after calling logoutAllDevices
    if (webSocket && webSocket.readyState === WebSocket.OPEN) {
      webSocket.close() // this does not close. investigate
      setWebSocket(undefined)
    }
    setIsWebSocketReady(false)
    lastPongDate.current = undefined
    setSessionData({} as Five9SessionData)
    setLoginState(undefined)
    setStation(undefined)
    setAgentAvailability(AgentAvailability.NOT_CONNECTED)
    setAgentPresence(undefined)
  }

  const makeCall = async (
    number: string,
    campaignId: string,
    contactId?: string
  ) => {
    await five9MakeExternalCall(
      sessionData.userId,
      {
        number,
        contactId,
        skipDNCCheck: !IS_PRODUCTION,
        campaignId,
        autoResolveDialingRules: false,
        checkMultipleContacts: false,
      } as any,
      getFive9RequestConfig()
    )
    // Upddate outreach metadata to inProgress
  }

  const answerCall = async (callId: string) => {
    await five9AnswerCall(sessionData.userId, callId, getFive9RequestConfig())
    // Upddate outreach metadata to inProgress
  }

  const rejectCall = async (callId: string) => {
    await five9RejectCall(sessionData.userId, callId, getFive9RequestConfig())
  }

  const holdCall = async (callId: string) => {
    await five9HoldCall(sessionData.userId, callId, getFive9RequestConfig())
  }

  const unholdCall = async (callId: string) => {
    await five9UnholdCall(sessionData.userId, callId, getFive9RequestConfig())
  }

  const parkCall = async (callId: string) => {
    await five9ParkCall(sessionData.userId, callId, getFive9RequestConfig())
  }

  const retrieveCall = async (callId: string) => {
    await five9RetrieveCall(sessionData.userId, callId, getFive9RequestConfig())
  }

  const addExternalParticipant = async (
    callId: string,
    number: string,
    warm = false
  ) => {
    await five9AddExternalParticipant(
      sessionData.userId,
      callId,
      number,
      getFive9RequestConfig(),
      warm
    )
  }

  const addCampaignToConference = async (
    callId: string,
    campaignId: string,
    warm = false
  ) => {
    await five9AddCampaignToConference(
      sessionData.userId,
      callId,
      campaignId,
      getFive9RequestConfig(),
      warm
    )
  }

  const leaveConference = async (callId: string, dispositionId: string) => {
    await five9LeaveConference(
      sessionData.userId,
      callId,
      dispositionId,
      getFive9RequestConfig()
    )
  }

  const completeWarmConference = async (callId: string) => {
    await five9CompleteWarmConference(
      sessionData.userId,
      callId,
      getFive9RequestConfig()
    )
  }

  const cancelWarmTransfer = async (callId: string) => {
    await five9CancelWarmTransfer(
      sessionData.userId,
      callId,
      getFive9RequestConfig()
    )
    await five9UnholdCall(sessionData.userId, callId, getFive9RequestConfig())
  }

  const disconnectConferenceParticipant = async (callId: string) => {
    await five9DisconnectConferenceParticipant(
      sessionData.userId,
      callId,
      getFive9RequestConfig()
    )
  }

  const disconnectAndDisposeCall = async (
    callId: string,
    dispositionId: string
  ) => {
    await five9DisconnectAndDisposeCall(
      sessionData.userId,
      callId,
      dispositionId,
      getFive9RequestConfig()
    )
  }

  const endFive9Call = async (callId: string) => {
    await five9EndCall(sessionData.userId, callId, getFive9RequestConfig())
  }
  const getOrgCampaigns = async () => {
    const campaigns = await five9GetOrgCampaigns(
      sessionData.orgId,
      getFive9RequestConfig()
    )
    setOrgCampaigns(campaigns)
    return campaigns
  }

  const getOrgReasonCodes = async () => {
    const reasonCodes = await five9GetOrgReasonCodes(
      sessionData.orgId,
      getFive9RequestConfig()
    )
    return reasonCodes
  }

  const transferToCampaign = async (
    callId: string,
    campaignId: string,
    warm: boolean
  ) => {
    await five9TransferToCampaign(
      sessionData.userId,
      callId,
      { campaignId, warm },
      getFive9RequestConfig()
    )
  }

  const completeTransfer = async (callId: string) => {
    await five9CompleteTransfer(
      sessionData.userId,
      callId,
      getFive9RequestConfig()
    )
  }

  const externalTransfer = async (
    callId: string,
    number: string,
    warm = false
  ) => {
    await five9ExternalTransfer(
      sessionData.userId,
      callId,
      number,
      warm,
      getFive9RequestConfig()
    )
  }

  const createContact = async (contactData: CreateContactPayload) => {
    const response = await five9CreateContact(
      sessionData.orgId,
      contactData,
      getFive9RequestConfig()
    )
    return response
  }

  const lookupContactByConsultId = async (consultId: string) => {
    const response = await five9GetContactByConsultId(
      sessionData.orgId,
      consultId,
      getFive9RequestConfig()
    )
    if (response.contacts.length > 0) {
      return response.contacts
    } else {
      return undefined
    }
  }

  const deleteContact = async (contactId: string) => {
    const response = await five9DeleteContact(
      sessionData.orgId,
      contactId,
      getFive9RequestConfig()
    )
    return response
  }

  const selectActiveContactById = async (contactId: string, callId: string) => {
    const response = await five9SelectActiveContactById(
      sessionData.userId,
      callId,
      contactId,
      getFive9RequestConfig()
    )
    return response
  }

  const lookupContactAndSelectActive = async (
    consult: Consult,
    callId: string
  ) => {
    let contacts = await lookupContactByConsultId(consult.id)
    if (!contacts?.length) {
      contacts = [
        await createContact({
          firstName: consult.patient.firstName,
          lastName: consult.patient.lastName,
          phoneNumbers: [
            {
              phoneType: 'primary',
              phoneNumber: consult.patient.contacts.phone,
            },
            {
              phoneType: 'sms',
              phoneNumber: consult.patient.contacts.sms,
            },
            ...(consult.patient.contacts.others?.map((contact) => ({
              phoneType: contact.type,
              phoneNumber: contact.number,
            })) || []),
          ],
          programName: consult.program?.name ?? '',
          state: consult.shippingAddress.state as string,
          zipCode: consult.shippingAddress.zip,
          emrLink: APP_ENDPOINT + `/consult/${consult.id}`,
          consultId: consult?.id as string,
        }),
      ]
    }
    if (contacts && contacts.length > 0) {
      return selectActiveContactById(contacts[0].id, callId)
    }
    return undefined
  }

  return (
    <Five9Context.Provider
      value={{
        sessionData,
        station,
        loginState,
        login,
        logoutAllDevices,
        five9Calls: calls,
        five9NewOutboundCall: newOutboundCall,
        five9InboundCall,
        five9DisconnectedCall: disconnectedCall,
        five9SessionActive,
        isWebSocketReady,
        isGettingSkills,
        getSkills,
        isGettingCalls,
        getCalls,
        makeCall,
        answerCall,
        rejectCall,
        holdCall,
        unholdCall,
        parkCall,
        retrieveCall,
        disconnectAndDisposeCall,
        getOrgCampaigns,
        transferToCampaign,
        completeTransfer,
        agentAvailability,
        agentSkills,
        orgCampaigns,
        updateAgentPresence,
        addExternalParticipant,
        completeWarmConference,
        setCalls,
        endFive9Call,
        five9TransferredCall: transferredCall,
        externalTransfer,
        cancelWarmTransfer,
        conferenceParticipantState,
        disconnectConferenceParticipant,
        createContact,
        lookupContactByConsultId,
        deleteContact,
        lookupContactAndSelectActive,
        addCampaignToConference,
        leaveConference,
      }}
    >
      {children}
    </Five9Context.Provider>
  )
}

export function useFive9Context() {
  return useContext(Five9Context)
}
